import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { library } from '@fortawesome/fontawesome-svg-core';
import FontIconPicker from '@fonticonpicker/react-fonticonpicker';
import '@fonticonpicker/react-fonticonpicker/dist/fonticonpicker.base-theme.react.css';
import '@fonticonpicker/react-fonticonpicker/dist/fonticonpicker.material-theme.react.css';
import './_styles_/IconField.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';
import Field from './Field';
import { extendedStateType } from '../../types';
import { resolveValue } from '../../utils/fields';
import { NO_STATE } from '../../hooks/useExtendedState';

IconField.propTypes = {
    label: PropTypes.node.isRequired,
    value: PropTypes.string,
    inputId: PropTypes.string,
    onChange: PropTypes.func,
    className: PropTypes.string,
    isRequired: PropTypes.bool,
    messageOnEmpty: PropTypes.string,
    onReset: PropTypes.func,
    isChanged: PropTypes.bool,
    renderFn: PropTypes.func,
    availableIcons: PropTypes.arrayOf(PropTypes.string),
    iconsPerPage: PropTypes.number,
    searchPlaceholder: PropTypes.string,
    noIconPlaceholder: PropTypes.string,
    noSelectedPlaceholder: PropTypes.string,
    state: extendedStateType,
};

IconField.defaultProps = {
    onChange: undefined,
    inputId: null,
    className: '',
    isRequired: false,
    messageOnEmpty: '',
    onReset: undefined,
    isChanged: undefined,
    renderFn: (iconName) => <FontAwesomeIcon icon={iconName} />,
    availableIcons: null,
    iconsPerPage: 100,
    searchPlaceholder: null,
    noIconPlaceholder: null,
    noSelectedPlaceholder: null,
    value: undefined,
    state: NO_STATE,
};

function IconField(props) {
    const {
        label,
        value,
        inputId,
        onChange,
        className,
        isRequired,
        messageOnEmpty,
        onReset,
        isChanged,
        renderFn,
        availableIcons,
        iconsPerPage,
        searchPlaceholder,
        noIconPlaceholder,
        noSelectedPlaceholder,
        state,
    } = props;
    const { t } = useTranslation();
    let icons = null;
    if (availableIcons === null) {
        const disabledIcons = ['font-awesome-logo-full'];
        icons = Object.keys(library.definitions.fas).filter((iconName) => !disabledIcons.find((forbidden) => forbidden === iconName));
    }
    const resolvedValue = resolveValue(value, state);
    const [displayedValue, setDisplayedValue] = useState(resolvedValue);
    const [error, setError] = useState(null);
    const stateValueSetter = state && state !== NO_STATE ? state.setValueIfNew : () => {};
    const setNewValue = (newValue) => {
        setDisplayedValue(newValue);
        if (isRequired && newValue === '') {
            setError(messageOnEmpty);
        }
        stateValueSetter(newValue);
        onChange && onChange(newValue);
    };
    useEffectOnUpdate(() => {
        const newResolvedValue = resolveValue(value, state);
        setNewValue(newResolvedValue);
    }, [value, state.value]);
    return (
        <Field
            label={label}
            inputId={inputId}
            error={error}
            onReset={onReset}
            isChanged={isChanged}
            state={state}
            isRequired={isRequired}
        >
            <div className={className}>
                <FontIconPicker
                    icons={availableIcons || icons}
                    theme="m7a"
                    renderFunc={renderFn}
                    value={displayedValue}
                    onChange={setNewValue}
                    isMulti={false}
                    searchPlaceholder={searchPlaceholder || t('icon-field.placeholder.search')}
                    noIconPlaceholder={noIconPlaceholder || t('icon-field.placeholder.no-icons-found')}
                    noSelectedPlaceholder={noSelectedPlaceholder || t('icon-field.placeholder.select-icon')}
                    iconsPerPage={iconsPerPage}
                    closeOnSelect
                />
            </div>
        </Field>
    );
}

export default IconField;
